import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["form", "alert"]
  static values = { 
    type: String, // Form type like "step-three-addresses" or "consolidated-addresses"
    requireAddresses: Boolean // Whether form requires at least one address per section
  }

  connect() {
    this.bindFormValidation()
    this.observeAddressChanges()
  }

  disconnect() {
    if (this.observer) {
      this.observer.disconnect()
    }
  }

  bindFormValidation() {
    this.element.addEventListener('submit', (event) => {
      this.validateForm(event)
    })
  }

  validateForm(event) {
    let isValid = true
    
    // Check form type for address validation rules
    const isConsolidatedEdit = this.typeValue === 'consolidated-addresses'
    const isStepThree = this.typeValue === 'step-three-addresses'
    
    // Only validate required sections if addresses are required
    if (this.requireAddressesValue) {
      const requiredSections = this.element.querySelectorAll('.card[data-required="true"]')
      const hasAddressSections = requiredSections.length > 0
      
      if (hasAddressSections && (isStepThree || (!isConsolidatedEdit && !isStepThree))) {
        isValid = this.validateRequiredAddressSections(requiredSections)
      }
    }
    
    // Add Bootstrap validation classes
    this.element.classList.add('was-validated')
    
    // If validation fails, prevent submit
    if (!isValid) {
      event.preventDefault()
      event.stopPropagation()
    }
  }

  validateRequiredAddressSections(requiredSections) {
    let isValid = true
    
    requiredSections.forEach(section => {
      const addressForms = section.querySelectorAll('.js-address-form')
      const validAddressForms = Array.from(addressForms).filter(form => {
        // Exclude forms marked for deletion
        const destroyField = form.querySelector('input[name*="[_destroy]"]')
        return !(destroyField && destroyField.value === '1')
      })
      
      if (validAddressForms.length === 0) {
        isValid = false
        this.addErrorStyling(section)
      } else {
        this.removeErrorStyling(section)
      }
    })
    
    if (!isValid) {
      this.showValidationAlert('Please add at least one address for each required section.')
      this.scrollToFirstError()
    }
    
    return isValid
  }

  addErrorStyling(section) {
    const cardHeader = section.querySelector('.card-header')
    if (cardHeader) {
      cardHeader.classList.add('border-danger', 'bg-danger', 'bg-opacity-10')
    }
  }

  removeErrorStyling(section) {
    const cardHeader = section.querySelector('.card-header')
    if (cardHeader) {
      cardHeader.classList.remove('border-danger', 'bg-danger', 'bg-opacity-10')
    }
  }

  showValidationAlert(message) {
    // Remove any existing alerts
    this.removeExistingAlerts()
    
    // Create new alert
    const alert = document.createElement('div')
    alert.className = 'alert alert-danger alert-dismissible fade show js-validation-alert'
    alert.setAttribute('role', 'alert')
    alert.innerHTML = `
      <i class="bi bi-exclamation-triangle-fill me-2"></i>
      <strong>Validation Error:</strong> ${message}
      <button type="button" class="btn-close" data-bs-dismiss="alert" aria-label="Close"></button>
    `
    
    // Insert at top of form
    this.element.insertBefore(alert, this.element.firstChild)
    
    // Auto-dismiss after 5 seconds
    setTimeout(() => {
      if (alert.parentNode) {
        alert.style.transition = 'opacity 0.3s'
        alert.style.opacity = '0'
        setTimeout(() => {
          if (alert.parentNode) {
            alert.remove()
          }
        }, 300)
      }
    }, 5000)
  }

  removeExistingAlerts() {
    const existingAlerts = this.element.querySelectorAll('.js-validation-alert')
    existingAlerts.forEach(alert => alert.remove())
  }

  scrollToFirstError() {
    const firstError = this.element.querySelector('.card-header.border-danger')
    if (firstError) {
      const offset = firstError.getBoundingClientRect().top + window.pageYOffset - 100
      window.scrollTo({
        top: offset,
        behavior: 'smooth'
      })
    }
  }

  observeAddressChanges() {
    // Use MutationObserver to remove error styling when addresses are added
    this.observer = new MutationObserver((mutations) => {
      mutations.forEach(mutation => {
        if (mutation.type === 'childList') {
          mutation.addedNodes.forEach(node => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              const addressForm = node.classList?.contains('js-address-form') ? 
                                 node : 
                                 node.querySelector?.('.js-address-form')
              
              if (addressForm) {
                const card = addressForm.closest('.card')
                if (card) {
                  this.removeErrorStyling(card)
                }
              }
            }
          })
        }
      })
    })
    
    // Observe all cards for new address forms
    const cards = this.element.querySelectorAll('.card')
    cards.forEach(card => {
      this.observer.observe(card, {
        childList: true,
        subtree: true
      })
    })
  }

  // Public method for external validation triggers
  validate() {
    const event = new Event('submit', { cancelable: true })
    this.validateForm(event)
    return !event.defaultPrevented
  }

  // Method to manually add/remove error styling (for integration with other controllers)
  markSectionAsValid(section) {
    this.removeErrorStyling(section)
  }

  markSectionAsInvalid(section) {
    this.addErrorStyling(section)
  }
}